"use client";
import { useState } from "react";
import { useAuthStore, AuthStep } from "@/app/auth/auth-store";
import ResponsiveLogo from "@/components/responsive-logo";
import { LoginRegisterToggle } from "@/components/login-register-toggle";
import { OtpLogin } from "@/components/auth/otp-login";
import { useRouter, useSearchParams } from "next/navigation";
import { toast } from "sonner";
import { useEffect } from "react";
import { LoadingScreen } from "@/components/layout/loading-screen";

interface AuthFlowProps {
  initialStep: AuthStep;
  FormComponent: React.ComponentType<any>;
  formProps: any;
  toggleType: "login" | "register";
}

export function AuthFlow({ initialStep, FormComponent, formProps, toggleType }: AuthFlowProps) {
  const authStep = useAuthStore((s: any) => s.authStep);
  const email = useAuthStore((s: any) => s.email);
  const setAuthStep = useAuthStore((s: any) => s.setAuthStep);
  const router = useRouter();
  const searchParams = useSearchParams();
  const nextUrl = searchParams.get("next");
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    setAuthStep(initialStep);
  }, [setAuthStep, initialStep]);

  // These should be passed in via formProps/onSubmit/onResend if they differ
  async function onSubmitOtp(email: string, otp: string) {
    if (formProps.onSubmitOtp) {
      const { error } = await formProps.onSubmitOtp(email, otp);
      if (error) {
        toast("Unable to login", { description: error });
        return { error };
      } else {
        // Show loading screen before redirect
        setIsRedirecting(true);
        // Redirect to the next URL if provided, otherwise go to home
        const redirectUrl = nextUrl ? decodeURIComponent(nextUrl) : "/home";
        router.push(redirectUrl);
      }
    }
  }

  async function onResendOtp(email: string) {
    if (formProps.onResendOtp) {
      const { error } = await formProps.onResendOtp(email);
      if (error) {
        toast("Failed to resend OTP", { description: error });
      }
    }
  }

  if (isRedirecting) {
    return <LoadingScreen message="Loading your recommendations..." />;
  }

  return (
    <div className="relative">
      <ResponsiveLogo />
      <div className="w-full max-w-md mx-auto">
        {(authStep === AuthStep.LOGIN || authStep === AuthStep.REGISTER) && (
          <div>
            <LoginRegisterToggle active={toggleType} />
            <FormComponent {...formProps} />
          </div>
        )}

        {authStep === AuthStep.OTP && email && (
          <OtpLogin
            email={email}
            onBack={() => setAuthStep(initialStep)}
            onSubmit={onSubmitOtp}
            onResend={onResendOtp}
          />
        )}
      </div>
    </div>
  );
}
